import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type {ColumnDef, Row} from "@tanstack/react-table";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import type {AggregationSurveyListItem} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {DataType, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {type Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {SurveyStatus, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {
    type SurveyDailyInvitation,
    type SurveyDailyListRow,
    SurveyDailyRowKind,
    SurveyDailySurveyState
} from "crqlar-react-common/dist/models/survey/surveyDailyList.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import SingleDatePicker from "../../common/newDatePicker/SingleDatePicker";
import {useDebouncedSingleDate} from "../../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableQueryKey,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {AccommodationReferences} from "../../common/uiElements/ResourceElement";
import {StayStatusElement} from "../../common/uiElements/StayStatusElement";
import {
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout
} from "../../layout/rightSidebar/RightSidebarRail";
import {StayStatusFilterValue, StayStatusRailFilter} from "../../layout/rightSidebar/StayStatusRailFilter";
import {
    SURVEY_DAILY_STATE_LABEL_KEYS,
    SurveyDailyInvitationLines,
    SurveyDailySentElement,
    SurveyDailyStatusCell,
    SurveyDailySurveysCell
} from "../daily/SurveyDailyCells";
import {formatSurveyDailyScore, isCancelledStay, roomNames} from "../daily/surveyDailyRows";
import SurveyInviteButton from "../share/SurveyInviteButton";
import SurveyInviteDialog from "../share/SurveyInviteDialog";
import SurveyInviteRailButton from "../share/SurveyInviteRailButton";
import {SurveyInvitationDialog} from "../share/SurveyInvitationLink";
import {SURVEY_DAILY_LIST_TABLE_ID} from "../shared/surveyQueryRefresh";
import {filterEnumValues, filterTextValues} from "../shared/surveyTableFilters";
import {useAllSurveys} from "../shared/useAllSurveys";
import {SurveyListSubmissionDialog} from "../submissions/SurveyListSubmissionDialog";

const GUEST_SURVEY_TYPES = [SurveyType.PRE_STAY, SurveyType.POST_STAY, SurveyType.PRE_SERVICE, SurveyType.POST_SERVICE];

// Code-unit order, as the API's name sort gave this filter (Mongo compares strings binary: uppercase first); then id.
const compareCodeUnits = (left: string, right: string) => (left < right ? -1 : left > right ? 1 : 0);
const byNameThenId = (left: AggregationSurveyListItem, right: AggregationSurveyListItem) => (
    compareCodeUnits(left.name ?? '', right.name ?? '') || compareCodeUnits(left.id, right.id)
);

const SURVEY_STATES = Object.values(SurveyDailySurveyState);
const RESERVATION_STATUSES = Object.values(ReservationStatus);

// A group's rooms are its children, as on the CRM daily list; a room on its own has none.
const getSubRows = (row: SurveyDailyListRow) => row.children;

const getRowId = (row: SurveyDailyListRow, _index: number, parent?: Row<SurveyDailyListRow>) => (
    parent ? `${parent.id}/${row.rowId}` : row.rowId
);

// The API reads the day in the organisation's timezone; midday keeps a browser in another zone on it.
const middayOf = (day: Date) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12);

function NoWrapText({value}: {value?: string}) {
    return <Typography variant={'body1'} noWrap>{value || ''}</Typography>;
}

interface ShareTarget {
    reservation: Reservation
    profileId: string
    roomName: string
}

/**
 * The hotel's stays on one day and whether each got its survey. It is the module's own list, not the
 * CRM daily list: it has to work without the CRM, and it is where staff hand a link to a guest.
 */
export default function SurveysDailyListTab() {
    useTrackScreenVisit('surveys daily list');

    const {t} = useTranslation();
    const appId = useCurrentAppId();
    const readOnly = useReadOnly();
    const {date: selectedDate, queryDate, onDateChange} = useDebouncedSingleDate(
        appId ? `surveys-daily-${appId}` : undefined
    );

    const [stayStatus, setStayStatus] = useState<StayStatusFilterValue>('ALL');
    const [currentProfile, setCurrentProfile] = useState<Profile>();
    const [shareTarget, setShareTarget] = useState<ShareTarget>();
    const [invitationId, setInvitationId] = useState<string>();
    const [submissionId, setSubmissionId] = useState<string>();

    const {surveys: allSurveys} = useAllSurveys();
    // Published guest surveys. filter() hands sort() a copy, so the shared list keeps its own order.
    const surveys = useMemo(
        () => allSurveys
            .filter(survey => survey.status === SurveyStatus.PUBLISHED
                && !!survey.type && GUEST_SURVEY_TYPES.includes(survey.type))
            .sort(byNameThenId)
            .map(survey => ({id: survey.id, name: survey.name})),
        [allSurveys]
    );

    // Under the table's own key on purpose: the review refresh that reloads the table reloads the open
    // submission with it, so the dialog never keeps a review state the server has already moved past.
    const submissionQuery = useQuery(
        [...serverTableQueryKey(SURVEY_DAILY_LIST_TABLE_ID), 'submission', submissionId],
        () => SurveyAggregationService.listSubmissions({id: [submissionId!], limit: {offset: 0, limit: 1}}),
        {
            enabled: !!submissionId,
            refetchOnWindowFocus: false,
            onSuccess: page => {
                if (page.content?.length) return;
                setSubmissionId(undefined);
                enqueueSnackbar(t('The submission could not be loaded.'), {variant: 'error'});
            },
            onError: error => {
                console.error('Could not open submission', error);
                setSubmissionId(undefined);
                enqueueSnackbar(t('The submission could not be loaded.'), {variant: 'error'});
            }
        }
    );
    const selectedSubmission = submissionId ? submissionQuery.data?.content?.[0] : undefined;

    // The invitation itself, read by id: it may have been sent without the reservation (an audience
    // flow), and a lookup by stay would miss it and offer a second link.
    const invitationQuery = useQuery(
        ['surveyDailyListInvitation', invitationId],
        () => SurveyService.invitationList({ids: [invitationId!], limit: {offset: 0, limit: 1}}),
        {
            enabled: !!invitationId,
            refetchOnWindowFocus: false,
            onSuccess: page => {
                if (page.content?.length) return;
                setInvitationId(undefined);
                enqueueSnackbar(t('This link could not be loaded.'), {variant: 'error'});
            },
            onError: error => {
                console.error('Could not open invitation', error);
                setInvitationId(undefined);
                enqueueSnackbar(t('This link could not be loaded.'), {variant: 'error'});
            }
        }
    );
    const selectedInvitation = invitationId ? invitationQuery.data?.content?.[0] : undefined;

    const openShare = useCallback(async (room: SurveyDailyListRow) => {
        if (!room.profileId || !room.reservationId || isCancelledStay(room)) return;

        try {
            const reservation = await ReservationService.getReservationById(room.reservationId);
            if (!reservation) throw new Error('Reservation not found');
            setShareTarget({reservation, profileId: room.profileId, roomName: roomNames(room)});
        } catch (error) {
            console.error('Could not open reservation', error);
            enqueueSnackbar(t('Failed to open reservation'), {variant: 'error'});
        }
    }, [t]);

    // An answer opens the answer and a link opens that link.
    const openInvitation = useCallback((invitation: SurveyDailyInvitation) => {
        if (invitation.submissionId) {
            setSubmissionId(invitation.submissionId);
            return;
        }
        setInvitationId(invitation.invitationId);
    }, []);

    // Rows open the staying guest; survey links and Create link keep their own actions.
    // Group rows have no guest of their own and expand through their chevron.
    const openProfile = useCallback(async (row: SurveyDailyListRow) => {
        if (row.kind === SurveyDailyRowKind.GROUP || !row.profileId) return;

        try {
            setCurrentProfile(await ProfileService.getProfileById(row.profileId));
        } catch (error) {
            console.error('Could not open profile', error);
            enqueueSnackbar(t('Could not load profile'), {variant: 'error'});
        }
    }, [t]);

    const columns = useMemo<ColumnDef<SurveyDailyListRow>[]>(() => [
        {
            id: 'guestName',
            header: t('Guest'),
            accessorFn: row => row.guestName,
            cell: ({row}) => (
                <Typography
                    variant={'body1'}
                    noWrap
                    sx={{fontWeight: row.original.kind === SurveyDailyRowKind.GROUP ? 600 : undefined}}
                >
                    {row.original.guestName || '—'}
                </Typography>
            ),
            size: 180,
            meta: {
                grow: true,
                isServerSortable: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'room',
            header: t('Room'),
            accessorFn: roomNames,
            cell: ({row}) => <AccommodationReferences resources={row.original.accommodationResources}/>,
            // Two rooms to a line, so a group row stays as low as on the CRM list.
            size: 140,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'start',
            header: t('Arrival'),
            accessorFn: row => row.start,
            cell: ({row}) => <DateElement date={row.original.start} dateOnly/>,
            size: 115,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.start} dateOnly/>
            }
        },
        {
            id: 'end',
            header: t('Departure'),
            accessorFn: row => row.end,
            cell: ({row}) => <DateElement date={row.original.end} dateOnly/>,
            size: 115,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.end} dateOnly/>
            }
        },
        {
            // A cancelled or no-show stay was never in house, so it shows that instead of where it would be.
            id: 'stayStatus',
            header: t('Stay status'),
            accessorFn: row => row.stayStatus,
            cell: ({row}) => (isCancelledStay(row.original)
                ? (
                    <ReservationStatusElement
                        status={row.original.reservationStatus}
                        start={row.original.start}
                        end={row.original.end}
                    />
                )
                : (
                    <StayStatusElement
                        stayStatus={row.original.stayStatus}
                        startDate={row.original.start}
                        endDate={row.original.end}
                    />
                )),
            size: 150,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => (isCancelledStay(row.original)
                    ? <ReservationStatusElement status={row.original.reservationStatus} asPrint/>
                    : <StayStatusElement stayStatus={row.original.stayStatus} asPrint/>)
            }
        },
        {
            id: 'survey',
            header: t('Survey'),
            cell: ({row}) => (
                <SurveyDailySurveysCell
                    row={row.original}
                    readOnly={readOnly}
                    onOpen={openInvitation}
                    onCreateLink={() => void openShare(row.original)}
                />
            ),
            size: 220,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SurveyDailyInvitationLines
                        invitations={row.original.invitations}
                        render={invitation => invitation.surveyName || '—'}
                        asPrint
                    />
                ),
                printOriginalCell: row => (
                    <SurveyDailyInvitationLines
                        invitations={row.original.invitations}
                        render={invitation => invitation.surveyName || '—'}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'surveyState',
            header: t('Status'),
            accessorFn: row => row.surveyState,
            cell: ({row}) => <SurveyDailyStatusCell row={row.original}/>,
            size: 140,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <SurveyDailyStatusCell row={row.original} asPrint/>
            }
        },
        {
            id: 'sentAt',
            header: t('Sent'),
            cell: ({row}) => (
                <SurveyDailyInvitationLines
                    invitations={row.original.invitations}
                    render={invitation => (
                        <SurveyDailySentElement
                            sentAt={invitation.sentAt}
                            sender={invitation.sentByName || t('Automation')}
                        />
                    )}
                />
            ),
            size: 180,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SurveyDailyInvitationLines
                        invitations={row.original.invitations}
                        render={invitation => (
                            <SurveyDailySentElement
                                sentAt={invitation.sentAt}
                                sender={invitation.sentByName || t('Automation')}
                                asPrint
                            />
                        )}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'score',
            header: t('Score'),
            cell: ({row}) => (
                <SurveyDailyInvitationLines
                    invitations={row.original.invitations}
                    align={'right'}
                    render={invitation => (
                        <Box component={'span'} sx={{fontVariantNumeric: 'tabular-nums'}}>
                            {formatSurveyDailyScore(invitation.score)}
                        </Box>
                    )}
                />
            ),
            size: 80,
            meta: {
                align: 'right',
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SurveyDailyInvitationLines
                        invitations={row.original.invitations}
                        render={invitation => formatSurveyDailyScore(invitation.score)}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'groupName',
            header: t('Group'),
            accessorFn: row => row.groupName,
            cell: ({row}) => <NoWrapText value={row.original.groupName}/>,
            size: 160,
            meta: {
                isServerSortable: true,
                printable: true
            }
        },
        {
            id: 'languageCode',
            header: t('Language'),
            accessorFn: row => row.languageCode,
            cell: ({row}) => (
                <LanguagesElement
                    languageCodes={row.original.languageCode ? [row.original.languageCode] : undefined}
                />
            ),
            size: 110,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => (
                    <LanguagesElement
                        languageCodes={row.original.languageCode ? [row.original.languageCode] : undefined}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'primaryEmail',
            header: t('Email'),
            accessorFn: row => row.primaryEmail,
            cell: ({row}) => <NoWrapText value={row.original.primaryEmail}/>,
            size: 220,
            meta: {
                printable: true
            }
        },
        {
            id: 'reservationCode',
            header: t('Reservation code'),
            accessorFn: row => row.reservationCode,
            cell: ({row}) => <NoWrapText value={row.original.reservationCode}/>,
            size: 160,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true
            }
        },
        {
            id: 'reservationStatus',
            header: t('Reservation status'),
            accessorFn: row => row.reservationStatus,
            cell: ({row}) => (
                <ReservationStatusElement
                    status={row.original.reservationStatus}
                    start={row.original.start}
                    end={row.original.end}
                />
            ),
            size: 170,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => <ReservationStatusElement status={row.original.reservationStatus} asPrint/>
            }
        }
    ], [openInvitation, openShare, readOnly, t]);

    const filterOptions = useMemo<FilterOption[]>(() => [
        {
            field: 'surveyState',
            name: t('Survey state'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: SURVEY_STATES.map(state => ({
                cid: state,
                name: t(SURVEY_DAILY_STATE_LABEL_KEYS[state]),
                colorCode: ''
            })),
            enumValues: SURVEY_STATES,
            dataType: DataType.ENUM
        },
        ...(surveys.length ? [{
            field: 'surveyId',
            name: t('Survey'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: surveys.map(survey => ({cid: survey.id, name: survey.name || survey.id, colorCode: ''})),
            enumValues: surveys.map(survey => survey.id),
            dataType: DataType.ENUM
        } satisfies FilterOption] : []),
        {
            field: 'language',
            name: t('Language'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: 'language_code_iso_639_1'
        },
        {
            field: 'reservationStatus',
            name: t('Reservation status'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: RESERVATION_STATUSES.map(status => ({
                cid: status,
                name: t(`Reservation status ${status}`),
                colorCode: ''
            })),
            enumValues: RESERVATION_STATUSES,
            dataType: DataType.ENUM
        }
    ], [surveys, t]);

    const controller = useServerTable<SurveyDailyListRow>({
        tableId: SURVEY_DAILY_LIST_TABLE_ID,
        columns,
        getRowId,
        getSubRows,
        onRowClick: openProfile,
        initialSorting: [{id: 'guestName', desc: false}],
        queryDependencies: [queryDate.getTime(), stayStatus],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await SurveyAggregationService.listDaily({
                date: middayOf(queryDate),
                query: searchQuery || undefined,
                stayStatuses: stayStatus === 'ALL' ? undefined : [stayStatus],
                surveyStates: filterEnumValues(filterRules, 'surveyState', SURVEY_STATES),
                surveyIds: filterTextValues(filterRules, 'surveyId'),
                languages: filterTextValues(filterRules, 'language'),
                reservationStatuses: filterEnumValues(filterRules, 'reservationStatus', RESERVATION_STATUSES),
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    const closeShare = () => {
        setShareTarget(undefined);
        void controller.query.refetch();
    };

    // The link dialog can void the link and make a new one, so the room has to be read again.
    const closeInvitation = () => {
        setInvitationId(undefined);
        void controller.query.refetch();
    };

    return (
        <>
            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <RightSidebarRailHeader>
                                <RightSidebarRailHeaderCenter>
                                    <SingleDatePicker value={selectedDate} onChange={onDateChange} size={'medium'}/>
                                </RightSidebarRailHeaderCenter>
                            </RightSidebarRailHeader>

                            <ServerTable/>

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <StayStatusRailFilter value={stayStatus} onChange={setStayStatus}/>
                                <ServerTableRailFilter options={filterOptions}/>
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <ServerTableRailPrint title={t('Daily List')} selectedDate={queryDate}/>
                                <SurveyInviteRailButton/>
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <ProfilePreviewDialog
                open={!!currentProfile}
                onClose={() => setCurrentProfile(undefined)}
                profile={currentProfile}
                extraActions={<SurveyInviteButton profileId={currentProfile?.id}/>}
                setProfile={profile => {
                    setCurrentProfile(profile);
                    void controller.query.refetch();
                }}
            />

            {/* Mounted only while open, so its survey selection starts afresh for every stay. */}
            {!!shareTarget && (
                <SurveyInviteDialog
                    open
                    onClose={closeShare}
                    reservation={shareTarget.reservation}
                    profileId={shareTarget.profileId}
                    roomName={shareTarget.roomName}
                />
            )}

            {!!selectedInvitation && (
                <SurveyInvitationDialog open invitation={selectedInvitation} onClose={closeInvitation}/>
            )}

            {!!selectedSubmission && (
                <SurveyListSubmissionDialog
                    submission={selectedSubmission}
                    onClose={() => setSubmissionId(undefined)}
                />
            )}
        </>
    );
}
